import { modeT } from './profiles';
import { hasSewer, sewerPipeCols, sewerPipeHead, sewerFitCols, sewerFitHead } from './sewer';
import { cur } from './units';
import { rowCalc, landCalc } from './garden';
import { FIX_SHORT, gnssQs } from './gnss';
import * as XLSX from 'xlsx';
import { I18N } from './i18n';
import { pipeLatLngs, pathLength } from './model';
import { download, projectStem } from './exporters';
import { getPrices, findPrice } from './license';
import { computePit, pitTotals, pitOverview, layerName, unitLabel } from './excavation';
import { drawSummary } from './drawStyles';
import { dailyTotals } from '../components/DailyReport';
import { materialRows } from './materialList';
import { gasLeaks, gasLeakRow, gasLeakHead, lelOf } from './utility';
import { trackStats } from './track';
import { logRows, logHead } from './trackModes';
import { kksPoints, kksRow, kksHead } from './inspect';
import { vatSplit } from './vat';
import { fill } from './license';
import { MAINT_KEYS, repairEntries } from './maint';
import { tl } from './terms';

const r2 = (n) => Math.round(n * 100) / 100;

export function networkTables(project, lang) {
  const t = modeT(tl(lang));
  const fittings = project.fittings || [];
  const fname = (pt) => (Array.isArray(pt) ? `${pt[0].toFixed(6)}, ${pt[1].toFixed(6)}` : fittings.find((f) => f.id === pt.fid)?.name || '?');
  const sew = hasSewer(project);
  const depthOf = (p) => (p.depth == null || p.depth === '' ? '' : r2(+String(p.depth).replace(',', '.')));
  const pipes = (project.pipes || []).map((p, i) => {
    const len = pathLength(pipeLatLngs(p, fittings));
    return {
      row: [i + 1, p.name, t['type_' + p.type], p.dn || '', p.material || '', depthOf(p), r2(len), fname(p.points[0]), fname(p.points[p.points.length - 1]),
        p.points.filter((x) => x.fid).map(fname).join(', '), p.points.length, ...(sew ? sewerPipeCols(p, fittings, t) : [])],
      key: `${t['type_' + p.type]}|${p.dn || '–'}`, len,
    };
  });
  const pipeHead = [t.colNo, t.colName, t.colType, 'DN', t.plMat, t.colDepthM, t.colLen, t.colFrom, t.colTo, t.colLinks, t.colPoints, ...(sew ? sewerPipeHead(t) : [])];
  const fitHead = [t.colNo, t.colName, t.colType, t.colState, t.colNote, t.colLat, t.colLon, t.colPhotos, t.colVideos, t.colMemos, t.sevTitle, 'GNSS Fix', `${t.gnssAcc} (m)`, t.gnssSats, 'HDOP', `${t.gnssGround} (m ${t.gnssNN})`, `${t.gnssPipe} (m)`, `${t.gnssAntenna}`, t.altLbl + ' (m)', t.altSrcLbl, t.mfColMfr, t.pcTitle, t.mfColDim, t.mfColSku, t.medTitle, t.gnssQual, t.gnssProfile, t.prExcReason, ...(sew ? sewerFitHead(t) : [])];
  const fitRows = fittings.map((f, i) => {
    const media = f.media || [];
    const g = f.gnss || {};
    return [f.pno ?? i + 1, f.pno != null && (f.code || f.sym) ? `[${f.code || f.sym}] ${f.name}` : f.name, t['fit_' + f.type], t['st_' + (f.state || 'none')], f.note || '', +f.lat.toFixed(f.gnss ? 8 : 7), +f.lon.toFixed(f.gnss ? 8 : 7),
      media.filter((m) => m.kind !== 'video').length, media.filter((m) => m.kind === 'video').length, (f.memos || []).length,
      f.type === 'leak' ? t['sev_' + (f.severity || 'medium')] : '', f.gnss ? FIX_SHORT[g.fix] || g.fix : '', g.acc ?? '', g.sats ?? '', g.hdop ?? '', g.ground ?? '', g.pipe ?? '', f.gnss ? g.antenna : '', f.elev?.v ?? '', f.elev ? t['altSrc_' + f.elev.src] || f.elev.src : '', f.prod?.mfr || '', f.prod?.name || '', f.prod?.dim || '', f.prod?.sku || '', f.med ? t['med_' + f.med] : '', t['qs_' + gnssQs(f.gnss, f.draft)] || '', g.profile ? t['prMode_' + g.profile] : '', g.reason || '', ...(sew ? sewerFitCols(f) : [])];
  });
  const byPipe = {};
  pipes.forEach((p) => { byPipe[p.key] = (byPipe[p.key] || 0) + p.len; });
  const byFit = {};
  fittings.forEach((f) => { const k = `${t['fit_' + f.type]}|${t['st_' + (f.state || 'none')]}`; byFit[k] = (byFit[k] || 0) + 1; });
  const byMat = {};
  const live = getPrices();
  project.reports.forEach((r) => r.photos.forEach((p) => Object.entries(p.inventory || {}).forEach(([k, n]) => {
    const price = p.inventoryPrices?.[k] ?? findPrice(live, k);
    byMat[k] = byMat[k] || { n: 0, amount: 0, price };
    byMat[k].n += n;
    if (price != null) byMat[k].amount += n * price;
  })));
  const matTotal = Object.values(byMat).reduce((s, m) => s + m.amount, 0);
  const summary = [
    [project.title], [],
    [t.sumPipes], [t.colType, t.colDn, t.colLen],
    ...Object.entries(byPipe).map(([k, v]) => [...k.split('|'), r2(v)]),
    [t.pipeTotal, '', r2(pipes.reduce((s, p) => s + p.len, 0))], [],
    [t.sumFittings], [t.colType, t.colState, t.colCount],
    ...Object.entries(byFit).map(([k, v]) => [...k.split('|'), v]),
    [t.invTotal, '', fittings.length],
  ];
  if (Object.keys(byMat).length) {
    summary.push([], [t.sumMaterial], [t.colType, t.colCount, t.colUnitPrice, t.colAmount],
      ...Object.entries(byMat).map(([k, m]) => [k, m.n, m.price != null ? r2(m.price) : '', m.price != null ? r2(m.amount) : '']),
      [t.invSum, '', '', r2(matTotal)]);
  }
  const repHead = [t.colNo, t.colName, t.colType, t.addrLabel, t.colLat, t.colLon, t.textTitle, t.sheetMaterial, t.colPhotos, t.colVideos];
  const repRows = project.reports.map((r, i) => {
    const mat = {};
    r.photos.forEach((p) => Object.entries(p.inventory || {}).forEach(([k, n]) => { mat[k] = (mat[k] || 0) + n; }));
    return [`#${i + 1}`, r.title, t.bnReport, r.address || '', +r.lat.toFixed(7), +r.lon.toFixed(7), (r.text || '').replace(/\s+/g, ' '),
      Object.entries(mat).map(([k, n]) => `${n}x ${k}`).join(', '), r.photos.filter((p) => p.kind !== 'video').length, r.photos.filter((p) => p.kind === 'video').length];
  });
  const vatAoa = (net, col) => {
    const v = vatSplit(net);
    const pad = (k, n) => [...Array(col - 1).fill(''), k, r2(n)];
    return [pad(t.vatNet, v.net), pad(fill(t.vatLine, { rate: v.rate }), v.vat), pad(t.vatGross, v.gross)];
  };
  const pitList = project.pits || [];
  const pitRows = [[t.colNo, t.colName, t.reports, t.pitArea, t.pitDepth, 'DN', t.pitColLayer, t.pitColThick, 'm³', 't', t.pitColPrice, t.pitColCost]];
  pitList.forEach((p, i) => {
    const c = computePit(p);
    const rep = project.reports.findIndex((x) => x.id === p.reportId);
    [...c.rows, c.excav].forEach((r, k) => pitRows.push([k ? '' : i + 1, k ? '' : p.name, k || rep < 0 ? '' : `#${rep + 1}`, k ? '' : r2(c.area), k ? '' : c.depth, k ? '' : p.dn || '',
      layerName(t, r), r.key === 'excav' ? '' : Math.round(r.thick * 100), r2(r.m3), r2(r.t), r.price != null ? `${r2(r.price)} ${cur()}/${unitLabel(r.unit)}` : '', r.cost != null ? r2(r.cost) : '']));
    pitRows.push(['', '', '', '', '', '', t.pitTotal, '', '', '', '', r2(c.total)]);
  });
  if (pitList.length) pitRows.push([], [...Array(10).fill(''), t.pitSum], ...vatAoa(pitTotals(pitList).cost, 11));
  if (pitList.length) {
    const tot = pitTotals(pitList);
    summary.push([], [t.pitSection], [t.pitColLayer, 'm³', 't', t.pitColCost],
      ...Object.values(tot.mats).map(({ row, m3, t: tn, cost }) => [layerName(t, row), r2(m3), r2(tn), r2(cost)]),
      [t.pitL_excav, r2(tot.excav), '', r2(tot.excavCost)], ...vatAoa(tot.cost, 3));
  }
  const ov = pitOverview(pitList, project.reports);
  const mt = (r) => (r ? [r2(r.m3), r2(r.t)] : ['', '']);
  const costRows = [[t.pitCostTitle, project.title], [],
    [t.pitCount, t.reports, 'm²', t.pitColExcav, `${t.pitColSand} m³`, `${t.pitColSand} t`, `${t.pitColGravel} m³`, `${t.pitColGravel} t`, `${t.pitColSurface} m²`, t.pitColCost],
    ...ov.rows.map((r) => [r.p.name, r.rep, r2(r.c.area), r2(r.c.excav.m3), ...mt(r.sand), ...mt(r.fill), r.surf ? r2(r.c.area) : '', r2(r.c.total)]),
    [], [t.pitByMat], [t.pitColLayer, 'm³', 't', t.pitColCost],
    ...Object.values(ov.tot.mats).map(({ row, m3, t: tn, cost }) => [layerName(t, row), r2(m3), r2(tn), r2(cost)]),
    [t.pitL_excav, r2(ov.tot.excav), '', r2(ov.tot.excavCost)], ...vatAoa(ov.tot.cost, 3)];
  const mat = materialRows(project);
  const matRows = [[t.reports, t.catName, t.catDim, t.catSku, t.colCount, t.colUnit, t.catPrice, t.catInstall, t.colAmount]];
  const addMat = (label, data) => data.rows.forEach((r) => matRows.push([label, r.name, r.dim, r.sku, r2(r.qty), t['unit_' + r.unit] || r.unit, r.price ?? '', r.install ?? '', r.amount != null ? r2(r.amount) : '']));
  project.reports.forEach((r, i) => addMat(`#${i + 1} ${r.title}`, materialRows(project, r.id)));
  if (mat.rows.length) {
    matRows.push([t.matProject]); addMat(t.matProject, mat); matRows.push(...vatAoa(mat.total, 8));
    summary.push([], [t.matList], [t.catName, t.colCount, t.colUnit, t.colAmount], ...mat.rows.map((r) => [r.label, r2(r.qty), t['unit_' + r.unit] || r.unit, r.amount != null ? r2(r.amount) : '']), ...vatAoa(mat.total, 3));
  }
  const drs = project.reports.filter((r) => r.daily && dailyTotals(r.daily).total > 0);
  if (drs.length) {
    summary.push([], [t.drTitle], [t.reports, t.drWeather, '°C', t.drCrew + ' h', t.drCrew + ` ${cur()}`, t.drMach + ' h', t.drMach + ` ${cur()}`, t.drSum + ` ${cur()}`]);
    drs.forEach((r) => { const d = dailyTotals(r.daily); summary.push([r.title, r.daily.weather ? t['drW_' + r.daily.weather] : '', r.daily.temp || '', r2(d.crewH), r2(d.crew), r2(d.machH), r2(d.mach), r2(d.total)]); });
    const all = drs.map((r) => dailyTotals(r.daily)).reduce((a, d) => a + d.total, 0);
    summary.push([t.drSum, '', '', '', '', '', '', r2(all)]);
  }
  const plan = materialRows(project, null, true), ds = drawSummary(project.drawings);
  if (plan.rows.length) summary.push([], [`${t.matList} – ${t.dsPlanQty}`], [t.catName, t.colCount, t.colUnit, t.colAmount], ...plan.rows.map((r) => [r.label, r2(r.qty), t['unit_' + r.unit] || r.unit, r.amount != null ? r2(r.amount) : '']));
  if (ds.length) summary.push([], [t.dsTitle], [t.dsLegend, t.dsPlanQty, t.dsExecQty, t.colUnit], ...ds.map((r) => [t['dsSt_' + r.style] || r.style, r2(r.plan), r2(r.built), r.kind === 'area' ? 'm²' : 'm']));
  return { t, matRows, pipes: [pipeHead, ...pipes.map((p) => p.row)], fittings: [fitHead, ...fitRows], reports: [repHead, ...repRows], summary, pits: pitRows, costRows };
}

const fileBase = projectStem;

export function exportXLSX(project, lang) {
  const { t, pipes, fittings, reports, summary, pits, matRows, costRows } = networkTables(project, lang);
  const wb = XLSX.utils.book_new();
  const add = (rows, name, widths) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = widths.map((w) => ({ wch: w }));
    let n = name.slice(0, 31), k = 2;
    while (wb.SheetNames.includes(n)) n = `${name.slice(0, 28)} (${k++})`;
    XLSX.utils.book_append_sheet(wb, ws, n);
  };
  add(summary, t.sheetSummary, [34, 20, 14]);
  add(pipes, t.sheetPipes, [5, 24, 16, 14, 11, 24, 24, 30, 8]);
  add(fittings, t.sheetFittings, [5, 22, 18, 16, 26, 13, 13, 7, 7, 12]);
  add(reports, t.sheetReports || t.reports, [5, 26, 22, 34, 13, 13, 50, 30, 7, 7]);
  if (matRows.length > 1) add(matRows, t.matList, [26, 36, 18, 16, 9, 8, 10, 10, 12]);
  if (pits.length > 1) add(pits, t.pitSection, [5, 20, 8, 10, 10, 7, 36, 9, 10, 10, 14, 12]);
  if (pits.length > 1) add(costRows, t.sheetCost, [30, 9, 10, 12, 10, 10, 10, 10, 13, 13]);
  const lr = logRows(project.tracks || [], t);
  if (lr.length) add([logHead(t), ...lr], t.tlSheet, [11, 26, 18, 18, 18, 18, 30, 8, 8, 9, 8, 10]);
  const gds = project.gardens || [];
  if (gds.length) {
    const r1 = (v) => Math.round((+v || 0) * 10) / 10;
    add([[t.gaTitle, 'm²', `${t.gaPer} (m)`, t.gaTabRow, t.gaPlants, `${t.ga_turf} (m²)`, `${t.ga_topsoil} (m³)`, `${t.ga_mulch} (m³)`],
      ...gds.map((g) => { const r = rowCalc(g.pts, g.row), l = landCalc(g.pts, g.land); return [g.name, r1(r.A), r1(r.per), r.rows, r.plants, r1(l.turf), r1(l.topsoil.m3), r1(l.mulch.m3)]; })], t.gaTitle, [28, 10, 12, 10, 10, 14, 14, 14]);
  }
  wb.Props = { Title: project.title, Author: 'GISS FieldRapport Pro', Company: 'GISS - Software Solutions' };
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  download(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${fileBase(project)}_${project.sector === 'garden' ? 'Schlagkartei' : 'Netzplan'}.xlsx`);
}

export function exportLogCSV(project, lang) {
  const t = I18N[lang] || I18N.de, rows = [logHead(t), ...logRows(project.tracks || [], t)];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n');
  download(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }), `${fileBase(project)}_Betriebstagebuch.csv`);
}

export function buildCSV(project, lang) {
  const { t, pipes, fittings, reports, summary } = networkTables(project, lang);
  const sep = ';';
  const cell = (v) => {
    const s = typeof v === 'number' && sep === ';' ? String(v).replace('.', ',') : String(v ?? '');
    return /[";,\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const block = (title, rows) => [[title], ...rows, []].map((r) => r.map(cell).join(sep)).join('\r\n');
  const csv = '\uFEFF' + [block(t.reports, reports), block(t.sheetFittings, fittings), block(t.sheetPipes, pipes), block(t.sheetSummary, summary)].join('\r\n');
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}

export const exportCSV = (project, lang) => download(buildCSV(project, lang), `${fileBase(project)}_Aufmass.csv`);

// work order list for the repair crew: all yellow / red parts
export function exportMaintXLSX(project, lang) {
  const t = I18N[lang] || I18N.de;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(t.locale) : '');
  const repNo = (id) => { const i = project.reports.findIndex((r) => r.id === id); return i >= 0 ? `#${i + 1} ${project.reports[i].title || ''}`.trim() : ''; };
  const mid = (o, kind) => {
    if (kind === 'pit') return o.center || o.pts?.[0];
    if (kind === 'pipe') { const p = pipeLatLngs(o, project.fittings); return p[Math.floor(p.length / 2)]; }
    return [o.lat, o.lon];
  };
  const objs = [...project.fittings.map((o) => [o, 'fitting', o.cat?.name || t['fit_' + o.type] || o.type]), ...(project.pipes || []).map((o) => [o, 'pipe', o.cat?.name || `DN ${o.dn || ''}`]),
    ...(project.pits || []).map((o) => [o, 'pit', t.pitCount])]
    .filter(([o]) => ['warn', 'defect'].includes(o.maint?.status))
    .sort((a, b) => MAINT_KEYS.indexOf(b[0].maint.status) - MAINT_KEYS.indexOf(a[0].maint.status));
  if (!objs.length && !repairEntries(project).length) return 0;
  const head = ['Nr.', t.mwoPart, t.mwoType, t.mwoStatus, t.mtTags, t.mwoNote, t.reports, 'Lat', 'Lon', 'Google Maps', t.mtLast, t.mwoDone];
  const rows = objs.map(([o, kind, type], i) => {
    const p = mid(o, kind) || [];
    const memo = (o.memos || []).map((m) => m.transcript).filter(Boolean).join(' | ');
    return [i + 1, `${o.name || ''}${o.serial ? ` [${o.serial}]` : ''}`, `${t['objKind_' + kind]} – ${type}`, t['mt_' + o.maint.status], (o.maint.tags || []).join(', '),
      [o.note, memo].filter(Boolean).join(' | '), repNo(o.reportId), p[0] != null ? +(+p[0]).toFixed(7) : '', p[1] != null ? +(+p[1]).toFixed(7) : '',
      p[0] != null ? `https://www.google.com/maps/search/?api=1&query=${(+p[0]).toFixed(7)},${(+p[1]).toFixed(7)}` : '', fmt(o.maint.date), ''];
  });
  const ws = XLSX.utils.aoa_to_sheet([[`${t.mwoTitle} – ${project.title}`], [`${t.mwoCreated}: ${new Date().toLocaleString(t.locale)} · ${rows.length} ${t.mwoItems}`], [], head, ...rows]);
  ws['!cols'] = [5, 24, 26, 22, 34, 40, 20, 12, 12, 46, 14, 22].map((w) => ({ wch: w }));
  rows.forEach((r, i) => { const c = ws[XLSX.utils.encode_cell({ r: i + 4, c: 9 })]; if (c && r[9]) c.l = { Target: r[9] }; });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t.mwoSheet.slice(0, 31));
  const reps = repairEntries(project);
  if (reps.length) {
    const rh = ['Nr.', t.mwoPart, t.mwoType, t.mtRepairDate, t.mtRepairBy, t.mtRepairPrev, t.mtTags, t.mtRepairNote, `${t.mtBefore} (${t.mtFile})`, `${t.mtAfter} (${t.mtFile})`];
    const rr = reps.map(({ o, kind, m, files }, i) => [i + 1, o.name || '', t['objKind_' + kind], fmt(m.repair.date), m.repair.by, t['mt_' + m.repair.prev] || '', (m.repair.prevTags || []).join(', '), m.repair.note || '',
      files.find((f) => f.which === 'before')?.name ? `media/${files.find((f) => f.which === 'before').name}` : '', files.find((f) => f.which === 'after')?.name ? `media/${files.find((f) => f.which === 'after').name}` : '']);
    const ws2 = XLSX.utils.aoa_to_sheet([[`${t.mtRepairs} – ${project.title}`], [], rh, ...rr]);
    ws2['!cols'] = [5, 24, 20, 14, 20, 22, 30, 36, 36, 36].map((w) => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws2, t.mtRepairs.slice(0, 31));
  }
  wb.Props = { Title: `${t.mwoTitle} – ${project.title}`, Author: 'GISS FieldRapport Pro' };
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  download(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${fileBase(project)}_Wartungsliste.xlsx`);
  return rows.length + reps.length;
}

// gas network inspection & leak protocol (gas leaks + pipe gas readings + inspection tracks)
export function exportGasXLSX(project, lang) {
  const t = I18N[lang] || I18N.de;
  const leaks = gasLeaks(project);
  const pipes = (project.pipes || []).filter((p) => p.type === 'gas' && (p.util?.ppm || p.util?.pl));
  const tracks = project.tracks || [];
  const kps = kksPoints(project);
  if (!leaks.length && !pipes.length && !kps.length) return 0;
  const rows = leaks.map((f, i) => gasLeakRow(f, t, i));
  const ws = XLSX.utils.aoa_to_sheet([[`${t.glPdfTitle} – ${project.title}`], [`${t.mwoCreated}: ${new Date().toLocaleString(t.locale)} · ${leaks.length} ${t.glLeaks}`], [], gasLeakHead(t), ...rows]);
  ws['!cols'] = [5, 24, 30, 10, 12, 10, 40, 30, 18, 12, 12].map((w) => ({ wch: w }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t.glLeaks.slice(0, 31));
  if (kps.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([kksHead(t), ...kps.map((f, i) => kksRow(f, t, i))]), 'KKS');
  if (pipes.length) {
    const ph = [t.objKind_pipe, 'DN', t.utPl, 'MOP (bar)', 'ppm', `% ${t.utLel}`, t.swLen];
    const pr = pipes.map((p) => [p.name || '', p.dn || '', p.util.pl ? t['utPl_' + p.util.pl] : '', p.util.mop ?? '', p.util.ppm ?? '', p.util.ppm ? +lelOf(p.util.ppm).toFixed(2) : '', +pathLength(pipeLatLngs(p, project.fittings)).toFixed(1)]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([ph, ...pr]), t.objKind_pipe.slice(0, 31));
  }
  if (tracks.length) {
    const th = [t.catName, t.hyDate, 'km', 'km/h'];
    const tr = tracks.map((x) => { const s = trackStats(x); return [x.name || '', new Date(s.start || Date.now()).toLocaleString(t.locale), +s.km.toFixed(2), +s.kmh.toFixed(1)]; });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([th, ...tr]), 'Routen');
  }
  wb.Props = { Title: `${t.glPdfTitle} – ${project.title}`, Author: 'GISS FieldRapport Pro' };
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  download(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${fileBase(project)}_Gasnetz-Protokoll.xlsx`);
  return leaks.length + pipes.length + kps.length;
}
